import useDelivery from '../components/composables/delivery-estimator';

function estimatedDeliveryDate(opts) {
  const config = {
    dom: {
      id: 'getItBy',
      text: 'getItByText',
      popup: 'updateZipcode',
      input: 'zipInput',
      hidden_input: 'deliveryInput',
      error: 'getItByError',
      submit: 'submitZipcode',
      zipcode: 'getItByZip',
    },
    cls: {
      hidden: 'hide',
    },
  };

  const c = __.extend(config, opts);
  const $main = document.getElementById(c.dom.id);
  const $text = document.getElementById(c.dom.text);
  const $popUp = document.getElementById(c.dom.popup);
  const $zipcode = document.getElementById(c.dom.zipcode);

  async function init() {
    if ($main && $text && $zipcode) {
      try {
        const { zipcode, fetchDeliveryEstimate } = useDelivery();
        const { estimatedDate } = await fetchDeliveryEstimate();
        const { deliveryEstimate } = estimatedDate;

        if (deliveryEstimate && zipcode?.value) {
          const { earliestPromisedDeliveryOn, latestPromisedDeliveryOn } = deliveryEstimate;

          if (earliestPromisedDeliveryOn || latestPromisedDeliveryOn) {
            setDate(earliestPromisedDeliveryOn || latestPromisedDeliveryOn);
            setZip(zipcode.value);
            showComponent();

            addEvent();
          }
        }
      } catch (err) {
        console.error({ message: 'Unable to set date', error: err });
      }
    }
  }

  /**
   * Set Date
   * @param {String} - date
   * @returns {void}
   * @description Sets the $text element's value
   */
  function setDate(date) {
    const isInitialValue = $text.innerText.includes('%{}%');
    const $input = document.getElementById(c.dom.hidden_input);

    if (!date) return;

    const formattedDate = new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });

    if (formattedDate && isInitialValue) {
      const text = $text.innerHTML.replace('%{}%', formattedDate);

      $text.innerHTML = text;
      $input.value = text ? `${text.trim()}` : '';
    }

    if (formattedDate && !isInitialValue) {
      const [newDateText] = $text.innerText.split(':');

      $text.innerText = `${newDateText}: ${formattedDate}`;
      $input.value = `${newDateText}: ${formattedDate}`;
    }
  }

  /**
   * Set Zip
   * @param {String} - zipcode
   * @returns {void}
   * @description Sets the $zipcode element's value
   */
  function setZip(zipcode) {
    if (!zipcode) return;

    $zipcode.innerText = zipcode;
  }

  /**
   * Show Component
   * @returns {void}
   * @description Removes the hidden class from the $main element
   */
  function showComponent() {
    __.removeClass($main, c.cls.hidden);
  }

  /**
   * Set New Zip
   * @returns {void}
   * @description Sets the new zipcode
   */
  async function setNewZip() {
    const $button = $popUp.querySelector(`#${c.dom.submit}`);
    const $updatedZipInput = $popUp.querySelector(`#${c.dom.input}`);
    const newZipcode = $updatedZipInput.value;

    if (!$updatedZipInput.checkValidity()) {
      showError();

      return;
    }

    if ($button) {
      __.addClass($button, 'is-loading');
      $button.setAttribute('disabled', true);
    }

    try {
      const { fetchDeliveryEstimate, updateZipcode } = useDelivery();
      const zipcode = await updateZipcode(newZipcode);
      const { estimatedDate } = await fetchDeliveryEstimate();
      const { deliveryEstimate } = estimatedDate;

      if (deliveryEstimate && zipcode) {
        const { earliestPromisedDeliveryOn, latestPromisedDeliveryOn } = deliveryEstimate;

        if (earliestPromisedDeliveryOn || latestPromisedDeliveryOn) {
          setDate(earliestPromisedDeliveryOn || latestPromisedDeliveryOn);
          setZip(zipcode);

          closePopUp();
        }
      }
    } catch (err) {
      console.error({ message: 'Unable to set date', error: err });
      closePopUp();
    }
  }

  /**
   * Show Error
   * @returns {void}
   * @description Shows the error message
   * and disables the submit button
   */
  function showError() {
    const $button = $popUp.querySelector(`#${c.dom.submit}`);
    const $error = $popUp.querySelector(`#${c.dom.error}`);

    if ($error) {
      __.removeClass($error, c.cls.hidden);

      if ($button) {
        $button.setAttribute('disabled', true);
      }
    }
  }

  /**
   * Remove Error
   * @returns {void}
   * @description Removes the error message
   * and enables the submit button
   */
  function removeError() {
    const $button = $popUp.querySelector(`#${c.dom.submit}`);
    const $error = $popUp.querySelector(`#${c.dom.error}`);

    if ($error) {
      __.addClass($error, c.cls.hidden);
    }

    if ($button) {
      $button.removeAttribute('disabled');
    }
  }

  /**
   * Open Pop Up
   * @returns {void}
   * @description Opens the pop up
   */
  function openPopUp() {
    if ($popUp) {
      const $input = $popUp.querySelector('input');
      __.removeClass($popUp, c.cls.hidden);
      $popUp.setAttribute('aria-hidden', false);

      $input.focus();
    }
  }

  /**
   * Close Pop Up
   * @returns {void}
   * @description Closes the pop up
   */
  function closePopUp() {
    const $button = $popUp?.querySelector(`#${c.dom.submit}`) || null;
    const $input = $popUp?.querySelector('input') || null;

    if ($button) {
      __.removeClass($button, 'is-loading');
      $button.removeAttribute('disabled');
    }

    if ($popUp) {
      __.addClass($popUp, c.cls.hidden);
      $popUp.setAttribute('aria-hidden', true);

      if ($input) {
        $input.value = '';
      }
    }
  }

  /**
   * Toggle Pop Up
   * @returns {void}
   * @description Toggles the pop up
   */
  function togglePopup() {
    if ($popUp && __.hasClass($popUp, c.cls.hidden)) {
      openPopUp();
    } else {
      closePopUp();
    }
  }

  /**
   * Click Outside
   * @param {Object} - event
   * @returns {void}
   * @description Closes the pop up if the user clicks outside of it
   */
  function clickOutside(e) {
    const isPopUpClosed = $popUp && __.hasClass($popUp, c.cls.hidden);

    if ($zipcode && !$zipcode.contains(e.target)) {
      if (!isPopUpClosed && !$popUp.contains(e.target)) {
        closePopUp();
      }
    }
  }

  /**
   * Add Event
   * @returns {void}
   * @description Adds the event listeners
   */
  function addEvent() {
    __.addEvent({
      id: c.dom.zipcode,
      event: 'click',
      fn: togglePopup,
    });

    __.addEvent({
      id: c.dom.input,
      event: 'keydown',
      fn: (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          setNewZip();
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          closePopUp();
        }

        removeError();
      },
    });

    __.addEvent({
      id: c.dom.submit,
      event: 'click',
      fn: (e) => {
        e.preventDefault();
        setNewZip();
      },
    });

    __.addEvent({
      id: document,
      event: 'click',
      fn: clickOutside,
    });
  }

  return init();
}

export default estimatedDeliveryDate;
